import React, { useEffect, useState } from 'react'
import PrivateRoute from 'containers/PrivateRoute'
import { RouteProps, Route, RouteComponentProps } from 'react-router-dom'
import { WorkspaceMinimalFragment, useGetPaymentDetailsQuery } from 'gqlTypes'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import GenericErrorView from 'views/GenericErrorView'
import { setActiveAuthorizationIdForWorkspace } from 'graphql/multisession'
import NotFoundView from 'views/NotFoundView'
import { mapSsoStartUrl, useUserAuthorizations } from 'modules/sso/operations'
import { setItem } from 'utils/localStorage'
import localStorageKeys from 'constants/localStorageKeys'
import TosMandatoryModal from 'modules/workspace/modals/TosMandatoryModal'
import { useGetWorkspaces } from 'modules/workspace/operations'
import { useApolloClient } from '@apollo/react-hooks'

export interface PrivateWorkspaceRouteExtraProps {
  workspace: WorkspaceMinimalFragment
  workspaces: WorkspaceMinimalFragment[]
}

interface WorkspaceDataProps extends PrivateWorkspaceRouteExtraProps {
  routeProps: React.PropsWithChildren<WorkspaceRouteProps>
}

const WorkspaceAuth: React.FC<WorkspaceDataProps> = ({
  workspace,
  workspaces,
  routeProps: { component: Component, ...routeProps },
}) => {
  const { authorizations, hasAccessToWorkspace } = useUserAuthorizations()

  const { customer, userIsOwner } = workspace

  // `Customer` is not available if the Workspace is a Personal type
  const { tosAgreed, ssoStartUrl, identifier: customerId } = customer || {}

  useEffect(() => {
    setItem(localStorageKeys.lastWorkspaceIdKey, workspace.identifier)
  }, [workspace.identifier])

  // We need to make sure this Workspace already has a payment method
  // before deciding if we show the TOS modal
  const { loading, error, data } = useGetPaymentDetailsQuery({
    variables: { customerId: customerId! },
    skip: !userIsOwner || tosAgreed! || !customer,
  })

  const shouldRedirectSso =
    authorizations.length > 0 && !hasAccessToWorkspace(workspace)

  if (shouldRedirectSso && ssoStartUrl) {
    window.location.href = mapSsoStartUrl(ssoStartUrl, {
      from: routeProps.location?.pathname,
    })

    return <DynamicLoadingPage />
  }

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error) {
    return <GenericErrorView error={error} />
  }

  const paymentMethodSet = data?.customer.paymentDetails.type !== 'NONE'
  const showTOSModal = customer && userIsOwner && !tosAgreed && paymentMethodSet

  if (showTOSModal) {
    return (
      <React.Suspense fallback={<DynamicLoadingPage />}>
        <TosMandatoryModal
          workspaceId={workspace.identifier}
          customerId={customerId!}
        />
      </React.Suspense>
    )
  }

  return (
    <Route
      {...routeProps}
      render={routeProps => (
        <Component
          {...routeProps}
          workspace={workspace}
          workspaces={workspaces}
        />
      )}
    />
  )
}

interface WorkspacesDataProps {
  workspaceId: string
  routeProps: React.PropsWithChildren<WorkspaceRouteProps>
}

const WorkspacesAuth: React.FC<WorkspacesDataProps> = ({
  workspaceId,
  routeProps,
}) => {
  const { loading, error, workspaces } = useGetWorkspaces()
  const apolloClient = useApolloClient()
  const [authId, setAuthId] = useState<string>()

  const workspace = (workspaces ?? []).find(
    workspace => workspace?.identifier === workspaceId
  )

  useEffect(() => {
    if (workspace && !loading && !error) {
      setActiveAuthorizationIdForWorkspace(
        {
          ssoEnabled: workspace.customer?.ssoEnabled || false,
          identifier: workspace.identifier,
        },
        apolloClient.cache
      )
      setAuthId(workspace.identifier)
    }
  }, [workspace, loading, error, apolloClient.cache])

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error) {
    return <GenericErrorView error={error} />
  }

  if (!workspace) {
    return <NotFoundView />
  }

  // Ensure workspace view is only rendered after auth is set
  if (authId !== workspace.identifier) {
    return <DynamicLoadingPage />
  }

  return (
    <WorkspaceAuth
      workspace={workspace}
      workspaces={workspaces ?? []}
      routeProps={routeProps}
    />
  )
}

export interface WorkspaceRouteProps
  extends OmitSafe<RouteProps, 'render' | 'component'> {
  component: React.ComponentType<
    RouteComponentProps<any> & PrivateWorkspaceRouteExtraProps
  >
}

export const PrivateWorkspaceRoute: React.FC<WorkspaceRouteProps> = props => {
  const { component, ...otherProps } = props

  return (
    <PrivateRoute
      {...otherProps}
      render={privateRouteProps => (
        <WorkspacesAuth
          workspaceId={privateRouteProps.match.params.workspaceId!}
          routeProps={props}
        />
      )}
    />
  )
}
